import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import {
  adminLogout,
  getSessionRoleForRestore,
  persistSessionRole,
  shouldSignOutOnVerificationFailure,
} from '../services/auth';
import { SYSTEM_LABELS, REPORTER_TYPES, USER_ROLES } from '../constants';
import { useModerationQueueQuery } from '../api/use-moderation-queue-query';
import { useAdminLoginMutation } from '../api/use-admin-login-mutation';
import { usePartnerLoginMutation } from '../api/use-partner-login-mutation';
import { useVerifyAdminSessionMutation } from '../api/use-verify-admin-session-mutation';
import { useVerifyPartnerSessionMutation } from '../api/use-verify-partner-session-mutation';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { teardownFcmWebRegistration } from '../services/fcmWebRegistration';
import { resetPortalColorMode } from '../styles/portalColorMode';
import { formatModerationDateTime } from '../utils/dateFormat';
// Create the AuthContext
const AuthContext = createContext();

// Create a provider component
export const AuthProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [firebaseUid, setFirebaseUid] = useState(null);

  const isAuthenticated = Boolean(userRole);
  const isAdmin = userRole === USER_ROLES.ADMIN;
  const isPartner = userRole === USER_ROLES.PARTNER;
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const [postsInitialized, setPostsInitialized] = useState(false);
  const {
    data: moderationQueueData,
    isLoading: moderationQueueLoading,
    isFetching: moderationQueueFetching,
    error: moderationQueueError,
    refetch: refetchModerationQueue,
  } = useModerationQueueQuery({
    enabled: isAdmin,
  });
  const adminLoginMutation = useAdminLoginMutation();
  const partnerLoginMutation = usePartnerLoginMutation();
  const verifyAdminSessionMutation = useVerifyAdminSessionMutation();
  const verifyPartnerSessionMutation = useVerifyPartnerSessionMutation();
  const verifyAdminSessionRef = useRef(verifyAdminSessionMutation.mutateAsync);
  const verifyPartnerSessionRef = useRef(verifyPartnerSessionMutation.mutateAsync);
  const isManualLoginRef = useRef(false);
  const hadFirebaseUserRef = useRef(false);

  verifyAdminSessionRef.current = verifyAdminSessionMutation.mutateAsync;
  verifyPartnerSessionRef.current = verifyPartnerSessionMutation.mutateAsync;

  // Format date and time as "25 May 2025 5:30pm"
  const formatDateTime = useCallback(
    (dateString) => formatModerationDateTime(dateString) || '',
    [],
  );

  // Convert violation types to normal case
  const normalizeViolationType = useCallback((type) => {
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setPosts([]);
      setPostsLoading(false);
      setPostsError(null);
      setPostsInitialized(true);
      return;
    }

    setPostsLoading(moderationQueueLoading || moderationQueueFetching);

    if (moderationQueueError) {
      setPostsError(moderationQueueError.message || 'Failed to fetch moderation queue');
      setPostsInitialized(true);
      return;
    }

    const queueItems = moderationQueueData?.moderationQueue?.items;
    if (!queueItems) return;

    // Status mapping for consistent display
    const STATUS_MAPPING = {
      pending_review: 'Under Review',
      approved: 'Approved',
      rejected: 'Rejected',
    };

    // Map API response to component format
    const mappedPosts = queueItems.map((item) => {
      // Determine if this is a chat message
      const isChatMessage =
        item.moderationType === 'chat_message' ||
        item.content?.type === 'chat_message' ||
        item.violationTypes?.includes('message');

      // Get content text - for posts use caption, for messages use text
      const contentText = isChatMessage
        ? item.content?.text || 'No message text available'
        : item.content?.caption || 'No caption available';

      return {
        id: item.id,
        postOwner: item.flaggedUser?.fullName || 'Unknown User',
        violationType:
          item.violationTypes?.map((type) => normalizeViolationType(type)).join(', ') || 'Unknown',
        violationDate: item.violationDateTime
          ? formatDateTime(item.violationDateTime)
          : formatDateTime(new Date().toISOString()),
        reportedBy: (() => {
          // Handle flaggedByUser as array
          if (Array.isArray(item.flaggedByUser) && item.flaggedByUser.length > 0) {
            const reporter = item.flaggedByUser[0];
            if (reporter?.fullName === REPORTER_TYPES.SYSTEM_MODERATION) {
              return SYSTEM_LABELS.SYSTEM_MODERATION;
            } else if (reporter?.fullName === REPORTER_TYPES.UNKNOWN_REPORTER) {
              return SYSTEM_LABELS.UNKNOWN_REPORTER;
            }
            return reporter?.fullName || SYSTEM_LABELS.UNKNOWN_REPORTER;
          }
          // Fallback for single object (in case API changes)
          if (item.flaggedByUser?.fullName) {
            if (item.flaggedByUser.fullName === REPORTER_TYPES.SYSTEM_MODERATION) {
              return SYSTEM_LABELS.SYSTEM_MODERATION;
            }
            return item.flaggedByUser.fullName;
          }
          return SYSTEM_LABELS.SYSTEM_MODERATION;
        })(),
        violationDescription: item.violationDescription || 'Content under review',
        status: STATUS_MAPPING[item.status] || item.status || 'Unknown Status',
        contentText: contentText,
        isChatMessage: isChatMessage,
        originalData: item,
      };
    });

    setPosts(mappedPosts);
    setPostsError(null);
    setPostsInitialized(true);
  }, [
    isAdmin,
    moderationQueueData,
    moderationQueueError,
    moderationQueueLoading,
    moderationQueueFetching,
    formatDateTime,
    normalizeViolationType,
  ]);

  // Manual refresh exposed to consumers
  const loadPosts = useCallback(async () => {
    await refetchModerationQueue();
  }, [refetchModerationQueue]);

  const applySessionProfile = useCallback((role, profile) => {
    const count = Number(profile?.notificationCount);
    setNotificationCount(Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0);

    if (role === USER_ROLES.ADMIN) {
      setUserRole(USER_ROLES.ADMIN);
      setAdminProfile(profile);
      setPartnerProfile(null);
      return;
    }

    if (role === USER_ROLES.PARTNER) {
      setUserRole(USER_ROLES.PARTNER);
      setPartnerProfile(profile);
      setAdminProfile(null);
      setPostsInitialized(true);
      setPosts([]);
      setPostsError(null);
    }
  }, []);

  const clearSession = useCallback(() => {
    setUserRole(null);
    setAdminProfile(null);
    setPartnerProfile(null);
    setNotificationCount(0);
    setPostsInitialized(true);
    setPosts([]);
    setPostsError(null);
  }, []);

  const decrementNotificationCount = useCallback(
    (amount = 1) =>
      setNotificationCount((count) => Math.max(0, count - Math.max(0, Number(amount) || 0))),
    [],
  );

  const resetNotificationCount = useCallback(() => setNotificationCount(0), []);

  const restoreNotificationCount = useCallback(
    (count) => setNotificationCount(Math.max(0, Number(count) || 0)),
    [],
  );

  const incrementNotificationCount = useCallback((amount = 1) => {
    const delta = typeof amount === 'number' && Number.isFinite(amount) ? amount : 1;
    setNotificationCount((count) => count + Math.max(0, delta));
  }, []);

  const restoreSessionRole = useCallback(async (pathname = window.location.pathname) => {
    const role = getSessionRoleForRestore(pathname);

    if (role === USER_ROLES.PARTNER) {
      try {
        const profile = await verifyPartnerSessionRef.current();
        persistSessionRole(USER_ROLES.PARTNER);
        return { role: USER_ROLES.PARTNER, profile };
      } catch (error) {
        const panelResult = error.panelResult ?? { success: false, error: error.cause };
        if (shouldSignOutOnVerificationFailure(panelResult)) {
          await firebaseSignOut(auth);
          persistSessionRole(null);
        }
        return {
          role: null,
          profile: null,
          message: error.message,
          recoverable: !shouldSignOutOnVerificationFailure(panelResult),
        };
      }
    }

    try {
      const profile = await verifyAdminSessionRef.current();
      persistSessionRole(USER_ROLES.ADMIN);
      return { role: USER_ROLES.ADMIN, profile };
    } catch (error) {
      const panelResult = error.panelResult ?? { success: false, error: error.cause };
      if (shouldSignOutOnVerificationFailure(panelResult)) {
        await firebaseSignOut(auth);
        persistSessionRole(null);
      }
      return {
        role: null,
        profile: null,
        message: error.message,
        recoverable: !shouldSignOutOnVerificationFailure(panelResult),
      };
    }
  }, []);

  // Restore session: Firebase user + admin or partner backend check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (hadFirebaseUserRef.current) {
          try {
            await teardownFcmWebRegistration({ skipApi: true });
          } catch (error) {
            console.warn('[FCM] Push teardown on auth sign-out failed:', error);
          }
        }
        hadFirebaseUserRef.current = false;
        setFirebaseUid(null);
        setSigningOut(false);
        clearSession();
        setLoading(false);
        return;
      }

      hadFirebaseUserRef.current = true;
      setFirebaseUid(user.uid);

      if (isManualLoginRef.current) {
        setLoading(false);
        return;
      }

      await auth.authStateReady();

      const session = await restoreSessionRole();

      if (session.role === USER_ROLES.ADMIN) {
        applySessionProfile(USER_ROLES.ADMIN, session.profile);
        setLoading(false);
      } else if (session.role === USER_ROLES.PARTNER) {
        applySessionProfile(USER_ROLES.PARTNER, session.profile);
        setLoading(false);
      } else {
        clearSession();
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [applySessionProfile, clearSession, restoreSessionRole]);

  const login = async (credentials) => {
    isManualLoginRef.current = true;
    persistSessionRole(USER_ROLES.ADMIN);
    try {
      await adminLoginMutation.mutateAsync(credentials);
      const profile = await verifyAdminSessionMutation.mutateAsync();
      applySessionProfile(USER_ROLES.ADMIN, profile);
      persistSessionRole(USER_ROLES.ADMIN);
      setFirebaseUid(auth.currentUser?.uid ?? null);
      return { success: true };
    } catch (error) {
      await firebaseSignOut(auth);
      return {
        success: false,
        message: error.message || 'Login failed. Please try again.',
      };
    } finally {
      isManualLoginRef.current = false;
    }
  };

  const partnerLogin = async (credentials) => {
    isManualLoginRef.current = true;
    persistSessionRole(USER_ROLES.PARTNER);
    try {
      await partnerLoginMutation.mutateAsync(credentials);
      const profile = await verifyPartnerSessionMutation.mutateAsync();
      applySessionProfile(USER_ROLES.PARTNER, profile);
      persistSessionRole(USER_ROLES.PARTNER);
      setFirebaseUid(auth.currentUser?.uid ?? null);
      return { success: true };
    } catch (error) {
      await firebaseSignOut(auth);
      return {
        success: false,
        message: error.message || 'Login failed. Please try again.',
      };
    } finally {
      isManualLoginRef.current = false;
    }
  };

  const signOut = async () => {
    setSigningOut(true);
    resetPortalColorMode();
    try {
      await adminLogout();
      persistSessionRole(null);
      clearSession();
    } catch (error) {
      setSigningOut(false);
      throw error;
    }
  };

  const updatePartnerProfile = useCallback((updater) => {
    setPartnerProfile((prev) => {
      if (!prev) return prev;
      return typeof updater === 'function' ? updater(prev) : updater;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        isAdmin,
        isPartner,
        adminProfile,
        partnerProfile,
        notificationCount,
        updatePartnerProfile,
        decrementNotificationCount,
        resetNotificationCount,
        restoreNotificationCount,
        incrementNotificationCount,
        login,
        partnerLogin,
        signOut,
        signingOut,
        loading,
        firebaseUid,
        posts,
        postsLoading,
        postsError,
        postsInitialized,
        loadPosts,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  return useContext(AuthContext);
};
